import { Button, Card, CardContent, CardHeader, Grid, Typography, Chip, Box, Divider } from "@mui/material";
import { useState } from "react";
import { apiConstants, getGeneric, postGeneric } from '../../common/constants'
import * as React from 'react'
import PageTitle from "../../components/PageTitle";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { useSnackbar } from "notistack";
import Loader from "../../common/Loader";
import { useForm } from "react-hook-form";
import { FormProvider, RHFSelectField } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import { RHFTextField } from "../../common/hook-form";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { formatDate } from "../../helpers/UtilityHelper";
import CallingFilterFields, {
    CALLING_FILTER_FORM_DEFAULTS,
    applyCallingFilterToForm,
    buildCallingFilterPayload,
    callingFilterColumn,
} from "./CallingFilterFields";

function GrowupNextConfig() {
    const [sizePerPage, setSizePerPage] = useState(10);
    const [page, setPage] = useState(0);
    const [configData, setConfigData] = useState([]);
    const [loading, setLoading] = useState(false)
    const { enqueueSnackbar } = useSnackbar();
    const [isData, setIsData] = useState(false);
    // Merchant-level "Use Netra" flag: score criteria in the filter are only allowed when it is on.
    const [netraEnabled, setNetraEnabled] = useState(true);

    const columns = [
        {
            field: 'push_lead',
            headerName: 'Push Lead',
            width: 120,
            flex: 0.6,
            renderCell: (col) => (
                <Chip
                    label={col?.row?.push_lead ? 'Enabled' : 'Disabled'}
                    color={col?.row?.push_lead ? 'success' : 'error'}
                    size="small"
                    variant="filled"
                />
            )
        },
        {
            field: 'tele_project_id',
            headerName: 'Project ID',
            width: 200,
            flex: 1,
            renderCell: (col) => col?.row?.tele_project_id
        },
        callingFilterColumn,
        {
            field: 'lastModifiedDate',
            headerName: 'Last Modified',
            width: 180,
            flex: 0.8,
            renderCell: (col) => formatDate(col.row.lastModifiedDate)
        },
        {
            field: "modifiedByUser",
            headerName: "Modified By",
            width: 200,
            flex: 1,
            renderCell: (col) => col?.row?.modifiedByUser
        },
    ]

    const methods = useForm({
        defaultValues: {
            merchant: '',
            ...CALLING_FILTER_FORM_DEFAULTS,
        }
    });

    const {
        handleSubmit,
        reset,
        getValues,
        setValue
    } = methods;

    const onSubmit = (data) => {
        const hadFilter = !!(configData[0]?.calling_filter);
        const { error, callingFilter } = buildCallingFilterPayload(data, hadFilter);
        if (error) {
            enqueueSnackbar(error, { variant: 'error' });
            return;
        }
        const payload = {
            push_lead: data.push_lead,
            tele_project_id: data.tele_project_id
        }
        if (callingFilter !== undefined) {
            payload.calling_filter = callingFilter;
        }
        setLoading(true);
        postGeneric(`${apiConstants.GROWUP_CONFIG}?merchantOid=${data.merchant}`, payload).then((res) => {
            setLoading(false);
            if (res.data?.status === "SUCCESS") {
                enqueueSnackbar("Success", { variant: 'success' });
                merchantFilter()
            } else {
                enqueueSnackbar(res.data?.errorMsg || "Some error occurred", { variant: 'error' });
            }
        }).catch((err) => {
            setLoading(false);
            enqueueSnackbar("Some error occurred", { variant: 'error' });
            console.log(err);
        })
    };

    const merchantFilter = () => {
        const id = getValues().merchant
        if (id) {
            setLoading(true)
            getGeneric(`${apiConstants.MERCHANT_DETAILS}/${id}`).then((res) => {
                setIsData(true)
                if (res.data?.growupNextNdrConfig) {
                    setConfigData([res.data?.growupNextNdrConfig])
                } else {
                    setConfigData([])
                }
                // Prefill so a save that only touches the filter round-trips the existing values
                // (the backend overwrites push_lead with whatever the payload deserializes to).
                setValue('push_lead', res.data?.growupNextNdrConfig?.push_lead ?? '');
                setValue('tele_project_id', res.data?.growupNextNdrConfig?.tele_project_id ?? '');
                applyCallingFilterToForm(setValue, res.data?.growupNextNdrConfig?.calling_filter);
                setLoading(false)
            }).catch((err) => {
                setLoading(false)
                console.log(err);
            })
            getGeneric(`${apiConstants.MERCHANT}/${id}`)
                .then((res) => setNetraEnabled(!!res.data?.useNetra))
                .catch(() => setNetraEnabled(true)); // unknown -> let the backend decide
        }
    }

    const hasConfig = configData?.length > 0;

    return (
        <div>
            {loading && <Loader />}
            <PageTitle>GrowupNext Configuration</PageTitle>
            <Grid sx={{ paddingBottom: 2 }}>

                {/* Merchant Selector */}
                <Card sx={{ mb: 2 }}>
                    <CardHeader
                        title="GrowupNext Configuration"
                        subheader="Configure NDR calling automation via GrowupNext for a merchant"
                        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                        subheaderTypographyProps={{ variant: 'body2' }}
                    />
                    <Divider />
                    <CardContent>
                        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <MerchantFilterCached name="merchant" label="Select Merchant" />
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <LoadingButton variant="contained" size="small" onClick={() => merchantFilter()}>
                                        Search
                                    </LoadingButton>
                                    <Button sx={{ ml: 1 }} variant="outlined" color="error" size="small"
                                        onClick={() => { reset(); setConfigData([]); setIsData(false) }}
                                    >
                                        Reset
                                    </Button>
                                </Grid>
                            </Grid>
                            {!isData && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                    Please select a merchant to view details
                                </Typography>
                            )}
                        </FormProvider>
                    </CardContent>
                </Card>

                {/* NDR Calling Section */}
                {isData && (
                    <Card>
                        <CardHeader
                            title={
                                <Typography variant="subtitle1" fontWeight={600}>NDR Calling</Typography>
                            }
                            subheader="Push NDR leads to GrowupNext for automated calling"
                            subheaderTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        />
                        <Divider />
                        <CardContent>
                            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={4}>
                                        <RHFSelectField width="100%" name="push_lead" label="Push Lead"
                                            options={[
                                                { value: true, label: 'Enable' },
                                                { value: false, label: 'Disable' }
                                            ]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <RHFTextField name="tele_project_id" label="Project ID" color="secondary" />
                                    </Grid>
                                </Grid>
                                <CallingFilterFields netraEnabled={netraEnabled} />
                                {/* Save sits after ALL fields (incl. the filter) so it isn't missed */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                    <LoadingButton type="submit" size="small" variant="contained">
                                        Save
                                    </LoadingButton>
                                </Box>
                            </FormProvider>

                            {hasConfig && (
                                <Box sx={{ mt: 2 }}>
                                    <ProzoDataGrid
                                        columns={columns}
                                        rows={configData.map((val, index) => ({ ...val, id: index }))}
                                        autoHeight={true}
                                        hideFooterPagination={true}
                                        sizePerPage={sizePerPage}
                                        setSizePerPage={setSizePerPage}
                                        setPage={setPage}
                                        page={page}
                                        paginationMode={'client'}
                                        getRowId={(row) => row.id}
                                        rowCount={configData?.length || []}
                                    />
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}
            </Grid>
        </div>
    );
}
export default GrowupNextConfig;
